import { Link } from 'react-router-dom';
import { get } from '../api';
import { useAuth } from '../auth';
import { useLoad } from '../hooks';

type Mode = 'off' | 'auto' | 'manual';

interface TemplateSummary {
  key: string;
  label: string;
  description: string;
  audience: 'customer' | 'contractor' | 'account';
  channels: ('email' | 'sms')[];
  email_mode: Mode;
  sms_mode: Mode;
  customised: { email: boolean; sms: boolean };
}

export const MODE_LABEL: Record<Mode, string> = { off: 'Off', auto: 'Automatic', manual: 'Manual' };

const AUDIENCE: Record<TemplateSummary['audience'], string> = {
  customer: 'To the customer',
  contractor: 'To the contractor',
  account: 'To the person signing in',
};

/** Manage → Templates: the wording of every message the app sends, one card each. */
export function Templates() {
  const { user } = useAuth();
  const canManageSettings = user?.capabilities.includes('teknika_manage_settings') ?? false;
  const list = useLoad(() => get<TemplateSummary[]>('/api/templates'), []);

  return (
    <>
      <h1>Templates</h1>
      <p className="muted">
        The wording of each message. Tags like {'{{visit.date}}'} are filled in when it is sent.
        {canManageSettings && (
          <>
            {' '}
            Choose which messages go out, and how, in{' '}
            <Link to="/settings?tab=messaging">Settings → Email &amp; SMS</Link>.
          </>
        )}
      </p>
      {list.loading && !list.data && <p>Loading…</p>}
      {list.error && (
        <p role="alert" className="error">
          {list.error}
        </p>
      )}
      {list.data && (
        <ul className="plain template-list">
          {list.data.map((t) => {
            const off = t.channels.every(
              (c) => (c === 'email' ? t.email_mode : t.sms_mode) === 'off',
            );
            return (
              <li key={t.key}>
                <Link to={`/templates/${t.key}`} className="template-card">
                  <h2>{t.label}</h2>
                  <p className="muted">{AUDIENCE[t.audience]}</p>
                  <p className="chips">
                    {t.channels.map((c) => (
                      <span key={c} className="chip">
                        {c === 'email' ? 'Email' : 'SMS'}:{' '}
                        {MODE_LABEL[c === 'email' ? t.email_mode : t.sms_mode]}
                      </span>
                    ))}
                    {(t.customised.email || t.customised.sms) && (
                      <span className="chip on">Customised</span>
                    )}
                  </p>
                  {off && <p className="muted">Turned off in Settings → Email &amp; SMS.</p>}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
